import { DesignData, MediaElement, MediaImage, Page } from 'editor/src/store/design/types';

import isImagePlaceholder from 'editor/src/util/design/isImagePlaceholder';
import notNull from 'editor/src/util/notNull';

export interface PdfPageTarget {
  pdfPage: number;
  spreadIndex: number;
  pageIndex: number;
  // an empty image placeholder on that page, which the PDF page fills
  placeholder?: MediaImage;
}

export interface PdfPagePlacement {
  // pages an image can be placed on
  productPages: number;
  // the product page of each PDF page that can be placed, in PDF page order
  targets: PdfPageTarget[];
  // 1-based numbers of the PDF pages that land on pages that are not printed
  skippedPdfPages: number[];
  // 1-based numbers of the PDF pages that land on pages with a design and no empty image placeholder
  designedPdfPages: number[];
}

const getCentre = (element: MediaElement) =>
  element.type === 'line'
    ? { x: (element.x1 + element.x2) / 2, y: (element.y1 + element.y2) / 2 }
    : { x: element.x + element.width / 2, y: element.y + element.height / 2 };

// A spread keeps all its elements on its first page, positioned across the whole spread.
const getPageMedia = (spreadMedia: MediaElement[], page: Page) =>
  spreadMedia.filter((element) => {
    const { x, y } = getCentre(element);
    return x >= page.x && x < page.x + page.width && y >= page.y && y < page.y + page.height;
  });

// PDF page N belongs on product page N, so a page that can't take it stays as it is instead of moving the rest.
// Only the first spread of a spread group counts, and an empty image placeholder takes an image, as in autofill.
const getPdfPagePlacement = (design: DesignData, pageImageIds: (string | null)[]): PdfPagePlacement => {
  const pdfPageCount = pageImageIds.length;
  const pdfImageIds = new Set(pageImageIds.filter(notNull));
  const firstSpreadsOfGroups = design.spread_groups?.length
    ? new Set(design.spread_groups.map((group) => group.spreadIndexes[0]))
    : undefined;
  const designPages = design.spreads.flatMap((spread, spreadIndex) =>
    !firstSpreadsOfGroups || firstSpreadsOfGroups.has(spreadIndex)
      ? spread.pages.map((page, pageIndex) => ({
          spreadIndex,
          pageIndex,
          printable: !!page.groups.content,
          media: getPageMedia(spread.pages[0].groups.media ?? [], page),
        }))
      : [],
  );
  const printablePages = designPages.filter(({ printable }) => printable);

  // A PDF made from the product's template has a page for every page, the unprinted inside covers too.
  const followsTemplate = pdfPageCount === designPages.length && printablePages.length < designPages.length;
  const placement: PdfPagePlacement = {
    productPages: printablePages.length,
    targets: [],
    skippedPdfPages: [],
    designedPdfPages: [],
  };
  (followsTemplate ? designPages : printablePages)
    .slice(0, pdfPageCount)
    .forEach(({ spreadIndex, pageIndex, printable, media }, index) => {
      const pdfPage = index + 1;
      const placeholder = media.find(isImagePlaceholder);
      if (!printable) {
        placement.skippedPdfPages.push(pdfPage);
      } else if (media.some((element) => element.type === 'image' && pdfImageIds.has(element.imageId))) {
        // Fill has already placed this PDF here, so the page is neither filled again nor counted as designed.
      } else if (placeholder || !media.length) {
        placement.targets.push({ pdfPage, spreadIndex, pageIndex, placeholder });
      } else {
        placement.designedPdfPages.push(pdfPage);
      }
    });

  return placement;
};

export default getPdfPagePlacement;
